import "../../../assets/css/clientAlerts/actionNeededList.css"

import { useAuth } from '../../../context/auth-user'
import { useNavigate } from 'react-router-dom'
import { useEffect, useReducer, useCallback, useMemo, useState } from 'react'
import moment from "moment"

import dots from "../../../assets/images/moreDots.svg"

import { FILTERS_MENU, reducer, ACTIONS, getRouteForAlert, getAlerts, getFiltersData, updateAlert } from "./utils"

import PageSelector from '../../pageSelector'
import FilterButton from './FilterButton'
import ActionNeededTable from "./ActionNeededTable"


export default function ActionNeededList() {
    // VARIABLE TO MANAGE THE NAVIGATE TO OTHER COMPONENTS
    const navigate = useNavigate()

    const { user } = useAuth()
    const userLogged = useMemo(() => user?.selectedProfile, [user])
    const darkLightMode = useMemo(() => user?.selectedProfile?.darkLightMode, [user])

    const [state, dispatch] = useReducer(reducer, {
        searchValue: "",
        appliedSearchValue: "",
        openFilter: null,
        activeFilters: [],
        filtersOptions: {
            supplier: [],
            typeOfAlert: [],
            client: []
        },
        filtersApplied: {
            supplier: [],
            typeOfAlert: [],
            client: []
        },
        // Newest first. Not "supplier": that is the filter menu key, not a field name
        // the API can sort by, so it fell through the backend sort switch into its
        // default branch and the screen opened on the oldest alerts in the system.
        sortOrder: "DSC",
        sortBy: "createdAt",
        originalAlerts: [],
        alerts: [],
        currentPage: 1,
        itemsPerPage: 20,
        showHistoric: false,
        moreOptionsOpen: null,
        totalAlerts: 0
    })

    const [filtersData, setFiltersData] = useState({
        supplier: [],
        client: []
    })

    const totalPages = useMemo(() => Math.ceil(state.totalAlerts / state.itemsPerPage), [state.totalAlerts, state.itemsPerPage]);

    // GET FILTERS DATA
    useEffect(() => {
        const controller = new AbortController()

        const { signal } = controller

        if (!userLogged) return

        const params = {
            rol: userLogged?.rol,
            dataID: userLogged?.dataID
        }

        getFiltersData({ params, signal }).then(data => {
            setFiltersData({
                supplier: data?.supplierFilter ?? [],
                client: data?.clientFilter ?? []
            })
        }).catch(err => {
            console.error(err)
        })

        return () => {
            controller.abort()
        }
    }, [userLogged])
    
    // get alerts for table
    useEffect(() => {
        const controller = new AbortController()

        const { signal } = controller;

        if (!userLogged) return
        
        const params = {
            rol: userLogged?.rol,
            dataID: userLogged?.dataID,
            searchValue: state.appliedSearchValue,
            page: state.currentPage,
            limit: state.itemsPerPage,
            order: state.sortOrder,
            orderBy: state.sortBy,
            showHistoric: state.showHistoric
        }

        if (state.filtersApplied.supplier.length > 0) {
            params.supplier = state.filtersApplied.supplier
        }

        if (state.filtersApplied.client.length > 0) {
            params.client = state.filtersApplied.client
        }

        if (state.filtersApplied.typeOfAlert.length > 0) {
            params.typeOfAlert = state.filtersApplied.typeOfAlert
        }

        getAlerts({ params, signal }).then(data => {
            dispatch({ type: ACTIONS.setAlerts, payload: data })
        }).catch(err => {
            console.error(err)
        })

        return () => {
            controller.abort()
        }
    }, [userLogged, state.appliedSearchValue, state.currentPage, state.itemsPerPage, state.sortOrder, state.sortBy, state.showHistoric, state.filtersApplied])


    // useEffect to add body listener
    useEffect(() => {
        function closeFilters(e) {
            if (!e.target.classList.contains("actionNeededFilter")) {
                dispatch({ type: ACTIONS.setOpenFilter, payload: null })
            }
        }

        function closeMoreOptions(e) {
            if (!e.target.classList.contains("alerts-more-dots")) {
                dispatch({ type: ACTIONS.handleMoreOptions, payload: null })
            }
        }

        document.body.addEventListener("click", closeFilters)
        document.body.addEventListener("click", closeMoreOptions)

        return () => {
            document.body.removeEventListener("click", closeFilters)
            document.body.removeEventListener("click", closeMoreOptions)
        }
    }, [])

    const handleSearchValue = useCallback((e) => {
        const { value } = e.target

        dispatch({ type: ACTIONS.handleSearch, payload: value })
    }, [])

    const handleApplySearchValue = useCallback((e) => {
        e.preventDefault();

        dispatch({ type: ACTIONS.applySearch })
        
    }, [])

    const handleShowHistoric = useCallback(() => {
        dispatch({ type: ACTIONS.handleShowHistoric })
    }, [])

    const handleSupplierClick = useCallback((alert) => {
        const routeForAlert = getRouteForAlert(alert)

        navigate(routeForAlert)
    }, [navigate])

    const handleMoreOptions = useCallback((alert, e) => {
        e?.stopPropagation()
        
        dispatch({ type: ACTIONS.handleMoreOptions, payload: alert._id })
    }, [])

    const handleActionCompleted = useCallback(async (action) => {
        try {
            const updatedAlert = await updateAlert(action, userLogged)
            
            dispatch({ type: ACTIONS.handleActionCompleted, payload: updatedAlert })
        } catch (error) {
            console.error("Error updating alert:", error)
        }
    }, [userLogged])

    const paginate = useCallback((pageNumber) => {
        if (pageNumber > totalPages) return

        dispatch({ type: ACTIONS.handleCurrentPage, payload: pageNumber })
    }, [totalPages])

    return (
        <section className={`actionNeeded ${darkLightMode ? "darkMode" : null}`}>
            <div className="filters">
                <div className="filtersButtons">
                    {FILTERS_MENU.map(filter => {
                        if (filter.accessKey === "supplier") {
                            filter.items = filtersData?.supplier
                        }

                        if (filter.accessKey === "client") {
                            filter.items = filtersData?.client
                        }

                        if (filter.accessKey === "client" && (userLogged?.rol === "Client" || userLogged?.rol === "Compliance manager")) {
                            return null
                        }

                        return (
                            <FilterButton
                                key={filter.id}
                                item={filter}
                                state={state}
                                dispatch={dispatch}
                            />
                        )
                    })}
                </div>

                <label className="switch">
                    <span id="label-on" className={`insideLabel ${state?.showHistoric ? "" : "hidden"}`}>View log</span>
                    <span id="label-off" className={`insideLabel ${state?.showHistoric ? "hidden" : ""}`}>View log</span>
                    <input type="checkbox" checked={state?.showHistoric} onChange={handleShowHistoric} />
                    <span className={`slider round ${state?.showHistoric ? "isChecked" : ""}`}></span>
                </label>

                <form onSubmit={handleApplySearchValue}>
                    <div className={`searchContainer ${darkLightMode ? "darkMode" : null}`}>
                        <input id="generalSearch" value={state.searchValue} type="text" placeholder="Search by name..."
                            onChange={handleSearchValue} />

                        <button className="applySearchButton" type="submit"></button>
                    </div>
                </form>
            </div>

            <ActionNeededTable
                state={state}
                dispatch={dispatch}
                handleActionCompleted={handleActionCompleted}
                handleMoreOptions={handleMoreOptions}
                darkLightMode={darkLightMode}
            />

            <div className="d-lg-none actionNeededList">
                {state?.alerts?.slice((state.currentPage * state.itemsPerPage) - state.itemsPerPage, state.currentPage * state.itemsPerPage)
                    .map((supplier, index) => {
                        const property = supplier.typeOfAction?.includes("Document") ? "documentName" : "clientName"
                        const dinamicText = supplier.typeOfAction?.includes("Document") ? "Document name" : "Client name"

                        const isOptionsOpen = state?.moreOptionsOpen === supplier._id
                        const actionLabel = supplier.completedBy ? "Mark as pending" : "Mark as completed"

                        return (
                            <div key={index} className="actionNeededRow" onClick={() => handleSupplierClick(supplier)}>
                                <h5 className="actionNeededName">
                                    {supplier.companyName || "Supplier not found"}

                                    <div className="mobileActionsContainer">
                                        <img className="alerts-more-dots" src={dots} alt="Prodensa Supplier" onClick={(e) => handleMoreOptions(supplier, e)} />

                                        <div className={`optionsContainer ${isOptionsOpen ? "visible" : null}`}>
                                            {supplier.companyName && <span onClick={() => handleSupplierClick(supplier)}>View action</span>}
                                            <span onClick={() => handleActionCompleted(supplier)}>{actionLabel}</span>
                                        </div>
                                    </div>
                                </h5>
                                <div className="actionNeededInformation">
                                    <div className="actionInfoContainer">
                                        <span className="actionNeededValue">{supplier.typeOfAction}</span>
                                        <span className="actionNeededTitle">Type Of Action</span>
                                    </div>

                                    <div className="actionInfoContainer">
                                        <span className="actionNeededValue">{moment(supplier.createdAt).format("DD MMMM YYYY")}</span>
                                        <span className="actionNeededTitle">Created At</span>
                                    </div>

                                    {supplier?.[property] && <div className="actionInfoContainer" id="idDocInfoMobile">
                                        <span className="actionNeededValue">{supplier?.[property]}</span>
                                        <span className="actionNeededTitle">{dinamicText}</span>
                                    </div>}

                                    <div className="actionInfoContainer">
                                        <span className="actionNeededValue" id="completedByUsername">{supplier.completedBy ?? "Not completed yet"}</span>
                                        <span className="actionNeededTitle">Completed By</span>
                                    </div>

                                    {supplier.completedBy && <div className="actionInfoContainer">
                                        <span className="actionNeededValue">{moment(supplier.updatedAt).format("DD MMMM YYYY hh:mm A")}</span>
                                        <span className="actionNeededTitle">Completed At</span>
                                    </div>}
                                </div>
                            </div>
                        )
                    })}
            </div>

            <div className="addAndPagination">
                <PageSelector
                    currentPage={state.currentPage}
                    totalItems={state.totalAlerts || 0}
                    itemsPerPage={state.itemsPerPage}
                    onChange={paginate}
                />
            </div>

            <span className="tableCount">{state.totalAlerts < state.itemsPerPage * state.currentPage ? state.totalAlerts : state.itemsPerPage * state.currentPage} of {state.totalAlerts}</span>
        </section>
    )
}
